import {
  useColorModeValue,
  Box,
  Text,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Button,
  Select,
  useToast,
  FormHelperText,
  FormErrorMessage,
} from '@chakra-ui/react';
import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { debounce } from 'lodash';
import type { InputActionMeta } from 'react-select';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatOptionLabelFilterPassTemplate } from '../../common/CustomReactSelect';
import { CustomReactSelect } from '../../common/CustomReactSelect';
import {
  useListPassTemplatesQuery,
  useGetPassTemplateQuery,
} from '../../../store/redux/api/templateApi';
import { MeContext } from '../../../context/MeContext';
import { MeContextState } from '../../../store/types/account';
import { CustomCsvDownloadIcon } from '../../../theme/icons/CustomCsvDownloadIcon';

import CustomToast from '../../../common/CustomToast';
import { getErrors } from '../../../utils/ajax';
import { useCreateBatchMutation } from '../../../store/redux/api/batchApi';
import {
  downloadBatchesCreateSampleCsv,
  BatchesCreateCsvStructure,
  buildBatchesCreateCsvStructure,
  BatchesCreateTemplateData,
} from './BatchesCreateSampleCSV';

interface PassTemplateOption {
  label: string;
  value: string;
}

type CsvStructure = BatchesCreateCsvStructure;

interface BatchCreateFormValues {
  batchName?: string;
  passTemplate?: PassTemplateOption | null;
  csvFile?: FileList | null;
}

interface ApiError {
  data?: {
    errors?: string[];
  };
}

const PASS_TEMPLATE_PAGE_SIZE = 100;

const BatchesCreateForm = () => {
  const modeLabelBg = useColorModeValue('#EFEFEF', 'primaryDark03');
  const modeFormBoxBg = useColorModeValue('gradient', 'secondaryDark');
  const modeBorderColor = useColorModeValue('primaryBackground', 'primaryDark');
  const [csvStructure, setCsvStructure] = useState<CsvStructure | null>(null);
  const toast = useToast();
  const meContext = useContext(MeContext) as { state: MeContextState };
  const navigate = useNavigate();

  const { filterEntity } = meContext.state;

  const [passTemplateOptions, setPassTemplateOptions] = useState<
    PassTemplateOption[]
  >([]);
  const [passTemplatesPage, setPassTemplatesPage] = useState(1);
  const [passTemplateSearch, setPassTemplateSearch] = useState('');
  const [passTemplateInputValue, setPassTemplateInputValue] = useState('');
  const [hasMorePassTemplates, setHasMorePassTemplates] = useState(false);

  const debouncedPassTemplateSearch = useMemo(
    () =>
      debounce((value: string) => {
        setPassTemplateSearch(value);
        setPassTemplatesPage(1);
        setPassTemplateOptions([]);
        setHasMorePassTemplates(false);
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedPassTemplateSearch.cancel();
    };
  }, [debouncedPassTemplateSearch]);

  useEffect(() => {
    setPassTemplateOptions([]);
    setPassTemplatesPage(1);
    setPassTemplateSearch('');
    setPassTemplateInputValue('');
    setHasMorePassTemplates(false);
    debouncedPassTemplateSearch.cancel();
  }, [filterEntity?.uuid, debouncedPassTemplateSearch]);

  const {
    data: templatesResponse,
    isError: isTemplatesError,
    error: templatesError,
    isFetching: isTemplatesFetching,
  } = useListPassTemplatesQuery({
    entityId: filterEntity?.uuid,
    fields: 'name',
    perPage: PASS_TEMPLATE_PAGE_SIZE,
    page: passTemplatesPage,
    search: passTemplateSearch || undefined,
  });

  useEffect(() => {
    if (!templatesResponse?.data) return;
    const mapped = templatesResponse.data.map(
      (item: { id: string; attributes: { name: string } }) => ({
        label: item.attributes.name,
        value: item.id,
      })
    );
    setPassTemplateOptions((prev) => {
      if (passTemplatesPage === 1) {
        return mapped;
      }
      const existingIds = new Set(prev.map((option) => option.value));
      return [
        ...prev,
        ...mapped.filter((option) => !existingIds.has(option.value)),
      ];
    });
    const totalCount = templatesResponse.meta?.totalCount;
    if (typeof totalCount === 'number') {
      setHasMorePassTemplates(
        passTemplatesPage * PASS_TEMPLATE_PAGE_SIZE < totalCount
      );
      return;
    }
    setHasMorePassTemplates(mapped.length === PASS_TEMPLATE_PAGE_SIZE);
  }, [templatesResponse, passTemplatesPage]);

  const handlePassTemplateInputChange = useCallback(
    (value: string, actionMeta: InputActionMeta) => {
      if (actionMeta.action === 'input-change') {
        setPassTemplateInputValue(value);
        debouncedPassTemplateSearch(value);
      }
      if (
        actionMeta.action === 'menu-close' ||
        actionMeta.action === 'input-blur' ||
        actionMeta.action === 'set-value'
      ) {
        setPassTemplateInputValue('');
        if (actionMeta.action === 'set-value') {
          debouncedPassTemplateSearch('');
        }
      }
      return value;
    },
    [debouncedPassTemplateSearch]
  );

  const handleLoadMorePassTemplates = useCallback(() => {
    if (isTemplatesFetching || !hasMorePassTemplates) {
      return;
    }
    setPassTemplatesPage((prev) => prev + 1);
  }, [hasMorePassTemplates, isTemplatesFetching]);

  useEffect(() => {
    if (isTemplatesError && templatesError) {
      const { message, code } = getErrors(templatesError);
      if (code !== 401) {
        toast({
          duration: 10000,
          render: (props) => (
            <CustomToast
              status="error"
              title={message ? message : `Something went wrong`}
              description={!message && 'Please try again later'}
              onClose={props.onClose}
            />
          ),
        });
      }
    }
  }, [isTemplatesError, templatesError, toast]);

  const validationSchema = Yup.object().shape({
    batchName: Yup.string().nullable(),
    passTemplate: Yup.mixed<PassTemplateOption>()
      .nullable()
      .required('Pass template is required'),
    csvFile: Yup.mixed<FileList>()
      .nullable()
      .required('CSV file is required')
      .test('fileType', 'Only .csv files are accepted', (value) => {
        return value?.[0]?.type === 'text/csv';
      }),
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<BatchCreateFormValues>({
    resolver: yupResolver(validationSchema),
    shouldUnregister: true,
    defaultValues: {
      batchName: '',
      passTemplate: null,
      csvFile: null,
    },
  });

  const watchCsvFile = watch('csvFile');
  const watchPassTemplate = watch('passTemplate');

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );

  const {
    currentData: templateResponse,
    isLoading: isLoadingTemplate,
    isError: isTemplateError,
    error: templateError,
  } = useGetPassTemplateQuery(
    {
      id: selectedTemplateId || '',
      fields: 'fields,appleEnhancedDesign',
    },
    {
      skip: !selectedTemplateId,
    }
  );

  useEffect(() => {
    if (!templateResponse?.data || !watchPassTemplate) {
      setCsvStructure(null);
      return;
    }

    const templateData = templateResponse.data
      .attributes as BatchesCreateTemplateData;

    setCsvStructure(
      buildBatchesCreateCsvStructure(templateData, watchPassTemplate.label)
    );
  }, [templateResponse, watchPassTemplate]);

  useEffect(() => {
    if (isTemplateError && templateError) {
      const { message, code } = getErrors(templateError);
      if (code !== 401) {
        toast({
          duration: 10000,
          render: (props) => (
            <CustomToast
              status="error"
              title={message ? message : 'Failed to prepare CSV structure'}
              description={!message && 'Please try again later'}
              onClose={props.onClose}
            />
          ),
        });
      }
    }
  }, [isTemplateError, templateError, toast]);

  const prepareSampleCsv = (selected?: PassTemplateOption | null) => {
    const selectedTemplate = selected ?? getValues('passTemplate');
    if (!selectedTemplate) return;

    setSelectedTemplateId(selectedTemplate.value);
  };

  const [createBatch, { isLoading: isSubmittingBatch }] =
    useCreateBatchMutation();

  const onSubmit = async (data: BatchCreateFormValues): Promise<void> => {
    try {
      const formData = new globalThis.FormData();
      if (data.batchName) {
        formData.append('name', data.batchName);
      }
      if (data.passTemplate) {
        formData.append('passTemplate', data.passTemplate.value);
      }
      if (data.csvFile && data.csvFile[0]) {
        formData.append('csv', data.csvFile[0]);
      }

      await createBatch(formData).unwrap();

      toast({
        render: (props) => (
          <CustomToast
            status="success"
            title="Batch creation started"
            description="Your passes are being created in the background"
            onClose={props.onClose}
          />
        ),
      });

      navigate('/passes/batches?tab=create');
    } catch (error: unknown) {
      const message = (error as ApiError)?.data?.errors?.[0];
      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="Failed to create batch"
            description={message || 'Please try again later'}
            onClose={props.onClose}
          />
        ),
      });
    }
  };

  return (
    <>
      <Box w="full" mx="auto" p={4}>
        <Box className="primaryBoxShadow" bg={modeLabelBg} borderRadius="15px">
          <Text
            fontSize="18px"
            textStyle="headingFamilyMedium"
            p="14px 14px 14px 24px"
            bg="white"
            borderRadius="15px 15px 0 0"
          >
            Create passes via CSV
          </Text>
          <Divider borderColor={modeBorderColor} />

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Flex direction={{ base: 'column', xl: 'row' }} p={8} gap={8}>
              {/* Left Column - Basic Info */}
              <Box flex="1">
                {/* Batch Name */}
                <FormControl mb={6} isInvalid={!!errors.batchName}>
                  <FormLabel mb={3}>Batch Name</FormLabel>
                  <Input
                    variant="filledForDarkBg"
                    id="batchName"
                    type="text"
                    autoComplete="off"
                    placeholder="Name your batch to help identify it"
                    {...register('batchName')}
                  />
                  {errors.batchName && (
                    <FormErrorMessage>
                      {errors.batchName.message}
                    </FormErrorMessage>
                  )}
                </FormControl>

                {/* Pass Template */}
                <FormControl
                  isRequired
                  mb={6}
                  isInvalid={!!errors.passTemplate}
                >
                  <FormLabel mb={3}>Pass Template</FormLabel>
                  <CustomReactSelect
                    options={passTemplateOptions}
                    placeholder={'Select pass template'}
                    noOptionsMessage={() => 'No pass templates found'}
                    menuPlacement="auto"
                    formatOptionLabel={formatOptionLabelFilterPassTemplate}
                    onInputChange={handlePassTemplateInputChange}
                    inputValue={passTemplateInputValue}
                    onMenuScrollToBottom={handleLoadMorePassTemplates}
                    isLoading={isTemplatesFetching}
                    onChange={(selected: PassTemplateOption | null) => {
                      setValue('passTemplate', selected, {
                        shouldValidate: true,
                      });
                      prepareSampleCsv(selected);
                    }}
                    value={watch('passTemplate')}
                    id="passTemplate"
                    passTemplate
                  />
                  {errors.passTemplate && (
                    <FormErrorMessage mt={2}>
                      {errors.passTemplate.message}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </Box>

              {/* Right Column - CSV Upload */}
              <Box
                flex="1"
                bg={modeFormBoxBg}
                p={6}
                borderRadius="md"
                border="1px dashed"
                borderColor={modeBorderColor}
              >
                <FormControl isRequired isInvalid={!!errors.csvFile}>
                  <FormLabel fontSize="lg" mb={4}>
                    CSV File Upload
                  </FormLabel>
                  <Text mb={4} fontSize="sm" color="gray.500">
                    Upload a CSV file containing customer data for pass
                    creation.
                  </Text>

                  <Button
                    as="label"
                    htmlFor="csv-upload"
                    cursor={watchPassTemplate ? 'pointer' : 'not-allowed'}
                    variant="outline"
                    size="md"
                    width="full"
                    mb={2}
                    bg="quinaryBackground"
                    borderColor={errors.csvFile ? 'error' : 'inherit'}
                    borderWidth={errors.csvFile ? '2px' : '1px'}
                    color={errors.csvFile ? 'red.500' : 'inherit'}
                    isDisabled={!watchPassTemplate}
                  >
                    {watchCsvFile && watchCsvFile[0]
                      ? watchCsvFile[0].name
                      : 'Choose File'}
                    <input
                      id="csv-upload"
                      type="file"
                      accept=".csv"
                      style={{ display: 'none' }}
                      {...register('csvFile')}
                      disabled={!watchPassTemplate}
                    />
                  </Button>

                  {errors.csvFile && (
                    <FormErrorMessage mb={2} mt={0}>
                      {errors.csvFile.message}
                    </FormErrorMessage>
                  )}

                  <FormHelperText>Max file size: 10MB</FormHelperText>

                  {watchPassTemplate && (
                    <Button
                      variant="link"
                      colorScheme="blue"
                      size="sm"
                      mt={4}
                      leftIcon={<CustomCsvDownloadIcon boxSize={6} />}
                      isDisabled={!csvStructure}
                      onClick={() =>
                        downloadBatchesCreateSampleCsv(csvStructure)
                      }
                      whiteSpace="normal"
                      textAlign="left"
                    >
                      Download Sample CSV Structure for{' '}
                      {watchPassTemplate.label}
                    </Button>
                  )}
                </FormControl>
              </Box>
            </Flex>

            {/* Submit Button */}
            <Flex
              justifyContent="flex-end"
              p={4}
              borderTop="1px solid"
              borderColor={modeBorderColor}
              bg="#FFFFFF"
              borderRadius="0 0 15px 15px"
            >
              <Button
                size="md"
                variant="secondary"
                as={RouterLink}
                to="/passes/batches"
                type="button"
                mr={4}
              >
                <Text>Cancel</Text>
              </Button>
              <Button
                type="submit"
                width={{ base: 'full', sm: 'auto' }}
                isLoading={isSubmittingBatch || isSubmitting}
                colorScheme="blue"
              >
                <Text>Create Passes</Text>
              </Button>
            </Flex>
          </form>
        </Box>
      </Box>
    </>
  );
};

export default BatchesCreateForm;
